import React, { useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment';
import parse from 'html-react-parser';
import { t } from 'i18next';
import { ReactComponent as EmptyIcon } from 'assets/images/icons/empty.svg';
import { getNotifications, markNotificationRead } from 'store/actions';
import './Notifications.scss';

const NotificationSkeleton = () => (
  <div className="notification-item-wrapper">
    <div className="notification_leftside">
      <div className="skeleton-line" style={{ width: '80%', height: '24px', marginBottom: '12px' }} />
      <div className="skeleton-line" style={{ width: '100%', height: '16px' }} />
    </div>
    <div className="notification_rightside">
      <div className="skeleton-line" style={{ width: '100%', height: '16px', marginBottom: '8px' }} />
      <div className="skeleton-line" style={{ width: '80%', height: '16px' }} />
    </div>
  </div>
);

// Single dashboard notification: collapsed to a few lines by default, with an inline
// expand/collapse toggle so the full message can be read without leaving the dashboard.
// The toggle only appears when the text actually overflows the clamp.
const NotificationCard = ({ n, onOpen }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const textRef = useRef(null);
  const openFiredRef = useRef(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;
    const measure = () => setOverflowing(el.scrollHeight > el.clientHeight + 2);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [n.notification_text]);

  const handleExpandClick = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !openFiredRef.current && n.is_read === false && onOpen) {
      openFiredRef.current = true;
      onOpen();
    }
  };

  return (
    <div className={`notification-item-wrapper${n.is_read === false ? ' notification-item--unread' : ''}`}>
      <div className="notification_leftside">
        <h4 className="notification_title">{n.notification_title}</h4>
        <div
          ref={textRef}
          className={`notification_text${expanded ? ' notification_text--expanded' : ''}`}>
          {parse(String(n.notification_text) || '')}
        </div>
        {(overflowing || expanded) && (
          <button
            type="button"
            className="notification_expand_btn"
            onClick={handleExpandClick}>
            {expanded ? t('notification_collapse') : t('notification_expand')}
          </button>
        )}
      </div>
      <div className="notification_rightside">
        <p className="notification_type">{n.notifications_type?.title ?? ''}</p>
        {n.notification_date && (
          <p className="notification_date_list">
            {moment(n.notification_date).format('HH:mm DD.MM.YYYY')}
          </p>
        )}
      </div>
    </div>
  );
};

const Notifications = ({ notifications }) => {
  const dispatch = useDispatch();
  const language = useSelector((state) => state.Layout.language);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(
      getNotifications({
        locale: language?.toLowerCase() || 'en',
        take: 20,
        skip: 0,
      })
    );
    // Используем setTimeout чтобы дать время для загрузки данных
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [dispatch, language]);

  const sortedNotifications = React.useMemo(() => {
    if (!notifications?.items?.length) {
      return [];
    }
    return [...notifications.items].sort((a, b) =>
      moment(b.notification_date).diff(moment(a.notification_date))
    );
  }, [notifications?.items]);

  const hasNotifications = sortedNotifications.length > 0;

  // Дашборд-превью: рендерим только первые несколько (одинаково на всех
  // вариациях — мобайл/планшет/десктоп), остальное — на странице /notifications.
  // Это представление (сколько карточек показываем), запрос/redux/сортировку не трогаем.
  const PREVIEW_COUNT = 4;
  const previewNotifications = sortedNotifications.slice(0, PREVIEW_COUNT);
  const totalCount = notifications?.total_items_count ?? sortedNotifications.length;
  const hasMore = totalCount > previewNotifications.length;

  return (
    <>
      <div className="notification_title_block">
        <h4 className="dashboard-block-title">{t('notification_title')}</h4>
      </div>
      <div className="notification-list-wrapper">
        {loading ? (
          <>
            <NotificationSkeleton />
            <NotificationSkeleton />
            <NotificationSkeleton />
          </>
        ) : (
          <>
            {!hasNotifications ? (
              <div className="referrals-zero" style={{ minHeight: 450 }}>
                <div>
                  <EmptyIcon />
                  <p>{t('notification_not_have_data')}</p>
                </div>
              </div>
            ) : (
              previewNotifications.map((n) => (
                <NotificationCard
                  key={n.id}
                  n={n}
                  onOpen={() => {
                    if (n.is_read === false) dispatch(markNotificationRead(n.id));
                  }}
                />
              ))
            )}
            {hasNotifications && hasMore && (
              <div className="notification-view-all-row">
                <Link to="/notifications" className="notification-view-all-btn">
                  {t('notification_view_all')}
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

Notifications.propTypes = {
  notifications: PropTypes.object,
};

const mapStateToProps = (state) => ({
  notifications: state.Notifications.notifications,
});

export default connect(mapStateToProps)(Notifications);
